// ===================================================
// contextMatcher.js — 과거 기록 관련성 매칭 유틸
//
// GPT를 전혀 사용하지 않고, 저장된 세션의 키워드와
// 사용자의 현재 입력 텍스트를 JavaScript로 비교해서
// 관련성 높은 과거 기록을 찾아냅니다.
//
// 토큰 절약 전략:
//   - 과거 기록 스캔에 GPT 토큰 0개 소모
//   - 매칭된 세션의 짧은 요약(contextSummary)만 GPT에 전달
//   - 프롬프트 추가 비용 최소화 (100~200 tokens 이내)
// ===================================================

// 모드명 한국어 표기
const MODE_LABELS = {
  diary:    '다이어리',
  note:     '노트',
  planner:  '플래너',
  study:    '학습',
  decision: '의사결정',
};

/**
 * 사용자의 현재 입력과 관련성 높은 과거 세션을 반환합니다.
 *
 * @param {string} currentText  - 사용자가 방금 입력한 텍스트
 * @param {Object} sessions     - 전체 저장 세션 { "날짜": [세션, ...] }
 * @param {number} maxCount     - 최대 반환 개수 (기본 3)
 * @param {string|null} preferMode - 우선 참고할 모드 (예: 'study').
 *                                   지정하면 해당 모드 기록에 가산점 +1을 부여해
 *                                   같은 키워드 매칭 수일 때 위로 올라옵니다.
 * @returns {{ date, mode, summary }[]} GPT에 전달할 과거 기록 배열
 */
export function findRelevantSessions(currentText, sessions, maxCount = 3, preferMode = null) {
  if (!currentText || !sessions) return [];

  const text = currentText.toLowerCase();
  const candidates = [];

  Object.entries(sessions).forEach(([date, dayArr]) => {
    dayArr.forEach(session => {
      // contextSummary가 저장된 세션만 사용 (이전에 저장된 기록은 요약 없음)
      if (!session.contextSummary) return;

      const keywords = session.keywords || [];
      if (keywords.length === 0) return;

      // 현재 텍스트에 해당 세션의 키워드가 몇 개나 포함되는지 계산
      const matchCount = keywords.filter(kw =>
        text.includes(kw.toLowerCase())
      ).length;

      if (matchCount > 0) {
        // preferMode와 같은 모드의 기록은 +1 가산점 부여 (동점 시 우선순위 상승)
        const score = matchCount + (preferMode && session.mode === preferMode ? 1 : 0);
        candidates.push({ date, session, matchCount: score });
      }
    });
  });

  // 관련도 높은 순 → 날짜 최신 순으로 정렬
  candidates.sort((a, b) => {
    if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
    return b.date.localeCompare(a.date);
  });

  // 상위 maxCount개를 GPT 전달 형식으로 변환
  return candidates.slice(0, maxCount).map(({ date, session }) => ({
    date:    formatRelativeDate(date),
    mode:    MODE_LABELS[session.mode] || session.mode,
    summary: session.contextSummary,
  }));
}

/**
 * 현재 보고 있는 기록과 키워드가 겹치는 연관 기록을 반환합니다.
 * SessionView에서 "연관 기록" 섹션을 표시할 때 사용합니다.
 *
 * @param {Object} currentSession - 현재 보고 있는 세션 객체 (id, keywords 포함)
 * @param {Object} sessions       - 전체 저장 세션 { "날짜": [세션, ...] }
 * @param {number} maxCount       - 최대 반환 개수 (기본 3)
 * @returns {{ date, idx, mode, initialText, contextSummary, matchedKeywords }[]}
 */
export function findRelatedSessions(currentSession, sessions, maxCount = 3) {
  if (!currentSession || !sessions) return [];

  const currentKeywords = currentSession.keywords || [];
  if (currentKeywords.length === 0) return [];

  // 현재 세션의 키워드를 소문자로 변환해서 비교 기준 생성
  const currentKwLower = currentKeywords.map(k => k.toLowerCase());
  const candidates = [];

  Object.entries(sessions).forEach(([date, dayArr]) => {
    dayArr.forEach((session, idx) => {
      // 자기 자신은 제외
      if (session.id === currentSession.id) return;
      if (!session.contextSummary) return;

      const sessionKeywords = session.keywords || [];
      if (sessionKeywords.length === 0) return;

      // 겹치는 키워드 찾기
      const matched = sessionKeywords.filter(kw =>
        currentKwLower.includes(kw.toLowerCase())
      );

      if (matched.length > 0) {
        candidates.push({ date, idx, session, matchCount: matched.length, matchedKeywords: matched });
      }
    });
  });

  // 겹치는 키워드 많은 순 → 최신 날짜 순으로 정렬
  candidates.sort((a, b) => {
    if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
    return b.date.localeCompare(a.date);
  });

  return candidates.slice(0, maxCount).map(({ date, idx, session, matchedKeywords }) => ({
    date,
    idx,
    mode: session.mode,
    initialText: session.initialText,
    contextSummary: session.contextSummary,
    matchedKeywords,
  }));
}


/**
 * 채팅용: 모든 세션을 수집해 최신순으로 반환합니다.
 * contextSummary가 있으면 그걸 사용하고, 없으면 initialText로 대체합니다.
 * chatWithMemory()에 전달할 전체 과거 기록 컨텍스트를 만들 때 사용합니다.
 *
 * @param {Object} sessions  - 전체 저장 세션 { "날짜": [세션, ...] }
 * @param {number} maxCount  - 최대 반환 개수 (기본 30, 토큰 한도 고려)
 * @returns {{ date, mode, summary }[]}
 */
export function buildAllSessionContexts(sessions, maxCount = 30) {
  if (!sessions) return [];

  const all = [];
  Object.entries(sessions).forEach(([date, dayArr]) => {
    dayArr.forEach(session => {
      // contextSummary가 있으면 사용, 없으면 initialText로 대체 (요약 생성 전 기록 포함)
      const summary = session.contextSummary || session.initialText;
      if (summary) {
        all.push({
          date:    formatRelativeDate(date),
          sortKey: date, // 원본 날짜 문자열 (정렬용)
          mode:    MODE_LABELS[session.mode] || session.mode,
          summary,
        });
      }
    });
  });

  // 최신 기록이 앞에 오도록 원본 날짜 기준으로 정렬
  all.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  return all.slice(0, maxCount).map(({ date, mode, summary }) => ({ date, mode, summary }));
}

// 날짜 문자열 "YYYY-MM-DD"를 상대적 표현("3일 전" 등)으로 변환
function formatRelativeDate(dateStr) {
  const target = new Date(dateStr);
  const today  = new Date();
  // 시간을 무시하고 날짜만 비교
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '어제';
  if (diffDays < 7)   return `${diffDays}일 전`;
  if (diffDays < 14)  return '1주 전';
  if (diffDays < 30)  return `${Math.floor(diffDays / 7)}주 전`;
  return `${Math.floor(diffDays / 30)}달 전`;
}
